import React from 'react';
import { Code2, Building2, Heart, ShoppingCart, GraduationCap, Factory, Truck, ArrowRight, Check } from 'lucide-react';
import { motion } from 'framer-motion';

export const LogicielsSurMesure = () => {
  const industries = [
    {
      icon: <Building2 className="w-6 h-6" />,
      title: "Construction",
      description: "Gestion de projets, suivi de chantiers, inventaire matériaux",
      gradient: "from-orange-500 to-red-500"
    },
    {
      icon: <Heart className="w-6 h-6" />,
      title: "Médical & Santé",
      description: "Dossiers patients, rendez-vous, gestion clinique",
      gradient: "from-blue-500 to-cyan-500"
    },
    {
      icon: <ShoppingCart className="w-6 h-6" />,
      title: "Commerce de détail",
      description: "Point de vente, inventaire, gestion clients",
      gradient: "from-green-500 to-emerald-500"
    },
    {
      icon: <GraduationCap className="w-6 h-6" />,
      title: "Éducation",
      description: "Gestion étudiants, cours, évaluations",
      gradient: "from-purple-500 to-pink-500"
    },
    {
      icon: <Factory className="w-6 h-6" />,
      title: "Manufacturier",
      description: "Production, qualité, chaîne d'approvisionnement",
      gradient: "from-gray-500 to-slate-500"
    },
    {
      icon: <Truck className="w-6 h-6" />,
      title: "Transport & Logistique",
      description: "Flotte, livraisons, gestion entrepôt",
      gradient: "from-yellow-500 to-orange-500"
    }
  ];

  const advantages = [
    "Solution 100% adaptée à vos processus",
    "Intégration avec vos systèmes existants",
    "Formation complète de votre équipe",
    "Support technique dédié",
    "Évolutivité selon vos besoins",
    "Propriété complète du code source"
  ];

  return (
    <section id="logiciels-sur-mesure" className="py-24 relative overflow-hidden bg-gradient-to-b from-[#050505] to-[#0A0A0A]">
      {/* Background Effects */}
      <div className="absolute inset-0 network-lines opacity-5"></div>
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#FF6B35] opacity-10 blur-[150px] rounded-full"></div>
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-blue-500 opacity-10 blur-[150px] rounded-full"></div>
      
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#FF6B35] mb-4 block">
            Division Logiciels
          </span>
          <h2 className="text-4xl sm:text-5xl font-black mb-6 tracking-tight">
            Solutions<span className="text-[#FF6B35]"> Sur Mesure</span>
          </h2>
          <p className="text-lg text-gray-400 max-w-3xl mx-auto">
            Des logiciels personnalisés conçus spécifiquement pour votre industrie et vos processus uniques. 
            Nous transformons votre vision en solution technologique performante.
          </p>
        </motion.div>

        {/* Featured Banner */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          className="glass rounded-2xl p-8 md:p-12 mb-16 border-2 border-[#FF6B35]/30 relative overflow-hidden group"
        >
          {/* Animated gradient background */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#FF6B35]/10 via-transparent to-blue-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
          
          <div className="relative z-10 grid md:grid-cols-2 gap-8 items-center">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-[#FF6B35] rounded-xl flex items-center justify-center">
                  <Code2 className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-2xl font-bold">Développement Sur Mesure</h3>
              </div>
              <p className="text-gray-400 text-lg mb-6">
                Chaque entreprise est unique. Nous créons des solutions logicielles qui s'adaptent parfaitement 
                à vos besoins spécifiques, qu'importe votre industrie.
              </p>
              <button 
                onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
                className="bg-[#FF6B35] text-white font-semibold px-8 py-4 rounded-lg hover:bg-[#FF8555] transition-all hover:shadow-[0_0_20px_rgba(255,107,53,0.4)] inline-flex items-center gap-2"
              >
                Discuter de votre projet
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
            
            {/* Advantages List */}
            <div className="grid grid-cols-2 gap-3">
              {advantages.map((advantage, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="flex items-start gap-2 text-sm"
                >
                  <div className="w-5 h-5 bg-[#FF6B35]/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-3 h-3 text-[#FF6B35]" />
                  </div>
                  <span className="text-gray-300">{advantage}</span>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Industries Grid */}
        <div>
          <h3 className="text-2xl font-bold text-center mb-8">
            Secteurs d'Activité <span className="text-[#FF6B35]">Spécialisés</span>
          </h3>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {industries.map((industry, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="group bg-[#121212] border border-white/10 rounded-xl p-6 hover:border-[#FF6B35]/50 transition-all duration-300 hover:-translate-y-1 relative overflow-hidden"
              >
                {/* Gradient overlay on hover */}
                <div className={`absolute inset-0 bg-gradient-to-br ${industry.gradient} opacity-0 group-hover:opacity-10 transition-opacity duration-300`}></div>
                
                <div className="relative z-10">
                  {/* Icon with gradient */}
                  <div className={`w-14 h-14 bg-gradient-to-br ${industry.gradient} rounded-xl flex items-center justify-center mb-4 text-white group-hover:scale-110 transition-transform duration-300`}>
                    {industry.icon}
                  </div>
                  
                  <h4 className="text-xl font-bold mb-2 group-hover:text-[#FF6B35] transition-colors">
                    {industry.title}
                  </h4>
                  <p className="text-gray-400 text-sm">
                    {industry.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Process Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-16 bg-[#121212]/50 border border-white/10 rounded-2xl p-8 md:p-12"
        >
          <h3 className="text-3xl font-bold text-center mb-12">
            Notre <span className="text-[#FF6B35]">Processus</span>
          </h3>
          
          <div className="grid md:grid-cols-4 gap-8">
            {[
              { step: "01", title: "Analyse", description: "Étude approfondie de vos besoins et processus" },
              { step: "02", title: "Conception", description: "Design UX/UI et architecture technique" },
              { step: "03", title: "Développement", description: "Création de votre solution sur mesure" },
              { step: "04", title: "Déploiement", description: "Formation, mise en ligne et support continu" }
            ].map((phase, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-[#FF6B35]/10 border-2 border-[#FF6B35] rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-2xl font-black text-[#FF6B35]">{phase.step}</span>
                </div>
                <h4 className="font-bold mb-2">{phase.title}</h4>
                <p className="text-sm text-gray-400">{phase.description}</p>
              </div>
            ))}
          </div>
        </motion.div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="mt-16 text-center"
        >
          <p className="text-lg text-gray-400 mb-6">
            Vous avez un projet logiciel en tête? Parlons-en!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
              className="bg-[#FF6B35] text-white font-semibold px-8 py-4 rounded-lg hover:bg-[#FF8555] transition-all hover:shadow-[0_0_20px_rgba(255,107,53,0.4)] inline-flex items-center justify-center gap-2"
            >
              Demander une consultation gratuite
              <ArrowRight className="w-5 h-5" />
            </button>
            <a 
              href="tel:+14503599394"
              className="bg-transparent border-2 border-white/20 text-white font-semibold px-8 py-4 rounded-lg hover:border-white transition-all inline-flex items-center justify-center gap-2"
            >
              Appelez-nous: (450) 359-9394
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
